import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Chip } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { importMethodChip, type ImportMethod } from './importHistoryMeta';

type ImportHistoryMethodChipProps = {
    entryId: string;
    /** True when bos-api wrote the invoice itself — see `isAutoInvoice`. */
    isAuto: boolean;
    method: ImportMethod;
};

/**
 * Small Chip cell for the `Method` column. Split into its own file so
 * the View stays a list-and-map.
 *
 * **Exactly one chip per row.** A system-written receipt used to show its
 * import method *and* an `Auto` badge — "Manual · Auto" on the same row, which
 * says two contradictory things about how the invoice got here (BOS-161).
 * `Auto` is not a second attribute of a manual import; it is the answer to the
 * same question, and it wins: nobody chose a method for a receipt the server
 * wrote itself, so naming one is worse than saying nothing.
 *
 * Renders as a `soft`-variant Chip — per design taste, soft chips are
 * the right surface for status / categorical badges sitting inside a
 * table row (they read as labels, not as actions).
 */
export const ImportHistoryMethodChip: React.FC<
    ImportHistoryMethodChipProps
> = ({ entryId, isAuto, method }) => {
    const { t } = useTranslation('pages');
    const display = importMethodChip(method, isAuto);

    return (
        <Chip
            color={display.color}
            size="sm"
            variant="soft"
            {...getNodeTestIdProp(`import-history-row-method-chip-${entryId}`)}
        >
            {display.icon ? (
                <Icon
                    className="size-3"
                    icon={display.icon}
                    {...getNodeTestIdProp(
                        `import-history-row-method-icon-${entryId}`,
                    )}
                />
            ) : null}
            <Chip.Label
                {...getNodeTestIdProp(
                    `import-history-row-method-label-${entryId}`,
                )}
            >
                {t(display.labelKey)}
            </Chip.Label>
        </Chip>
    );
};
